"use client";
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import Button from "antd/es/button";
import Text from "antd/es/typography/Text";

const LeftIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" />
  </svg>
);
import {
  filterBusinessServices,
  resolveCatalogRequirementLabel,
} from "@openings/booking-core";
import type {
  LocationOpeningsItem,
  DbDateString,
  ServiceDTO,
} from "@openings/api-client";

import DateSelector from "../components/DateSelector";
import { useBookingDate } from "../components/DateSelector/useBookingDate";
import SelectService from "../components/SelectService";
import { LocationStep } from "../LocationStep";
import ConsultationDrawer from "../ConsultationDrawer";
import ServiceRequestForm from "../ServiceRequestForm";
import type { ConsultationService } from "../ServiceRequestForm";
import { useDrawerBack } from "../MobileBookingDrawer";
import { DesktopBookingDrawer } from "../DesktopBookingDrawer";
import {
  bookingColumnStyle,
  bookingStackGapStyle,
  bookingSectionTitleStyle,
  bookingPageTitleStyle,
  bookingSmallStyle,
  selectedLocationSummaryStyle,
  selectedLocationCopyStyle,
  locationChangeButtonStyle,
  appointmentHeaderStyle,
  appointmentHeaderTitleStyle,
} from "../bookingStyles";
import type {
  BookingAppointmentForm,
  BookingScheduleBookingProps,
  SelectedService,
} from "../types";
import { ScheduleMemberOpeningsDisplay } from "./ScheduleMemberOpeningsDisplay";
import { bookingTimeZoneFor } from "../bookingTimeZone";

/**
 * The schedule-scoped service catalog. `unscoped` is the only state that may
 * show the business-wide list: it means no schedule is chosen, or the client
 * cannot fetch a schedule's services at all. `loading` and `failed` carry
 * whatever this same schedule already returned, so a read in flight and a
 * read that fails — however many times — keep that list and never widen to
 * another schedule's or the business-wide one (BF-DRIFT-019).
 */
type ScheduleCatalog =
  | { status: "unscoped" }
  | { status: "loading"; scheduleId: string; services: ServiceDTO[] | null }
  | { status: "ready"; scheduleId: string; services: ServiceDTO[] }
  | { status: "failed"; scheduleId: string; services: ServiceDTO[] | null };

const UNSCOPED_CATALOG: ScheduleCatalog = { status: "unscoped" };
const NO_SERVICES: ServiceDTO[] = [];

function formatDate(date: Date): DbDateString {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}` as DbDateString;
}

/**
 * Schedule-mode booking component. Used by schedule/appointment pages
 * and single/multi-page templates.
 *
 * Shows:
 * 1. LocationStep (when multiple schedules)
 * 2. Date picker
 * 3. Service selector (schedule-specific services when available)
 * 4. Member panels with time slots
 */
const BookingScheduleBooking = ({
  salonId,
  services,
  schedules,
  cancellationPolicyEnabled,
  cancellationPolicyHours,
  cancellationPolicyNote,
  lateGraceMinutes,
  verificationMethod,
  bookingHorizonDays,
  bookingTimeZone,
  openingsClient,
  openAppointmentForm,
  submitServiceRequest,
  uploadMedia,
  // completionMode is consumed by the web wrapper, not used directly here
  hasTeamMembers = true,
  showSchedule = false,
  showLocationSummary = false,
  showAvailabilityStatus = false,
  renderInlineAppointment,
  onScheduleSelect,
  initialScheduleId,
  memberPageBaseHref,
}: BookingScheduleBookingProps) => {
  // Refresh counter — bumped after a booking is completed so the
  // `ScheduleMemberOpeningsDisplay` re-fetches time slots once the modal
  // closes, hiding the slot the user just took.
  const [refreshCount, setRefreshCount] = useState(0);
  // Tracks whether the currently-open inline appointment flow resulted in a
  // successful booking. When the user closes the drawer, we use this to
  // decide whether to refresh slots.
  const bookingCompletedRef = useRef(false);

  // Inline appointment flow state: in place on mobile, in the desktop drawer
  // overlay on desktop. `desktop` is the layout when the flow opened; the flow
  // keeps it until it closes, because moving it between the two parents when
  // the width crosses 768px remounts it and drops its step, entered contact,
  // code and create idempotency key (BF-DRIFT-021).
  const [inlineAppointment, setInlineAppointment] = useState<{
    form: BookingAppointmentForm;
    desktop: boolean;
  } | null>(null);
  const inlineAppointmentForm = inlineAppointment?.form ?? null;

  // Inline consultation flow state (mobile — renders RequestForm inside drawer)
  const [inlineConsultation, setInlineConsultation] = useState<{
    userId?: string;
    teamMemberId?: string;
    scheduleId?: string;
    userName: string;
    services: ConsultationService[];
  } | null>(null);

  // Desktop consultation modal state
  const [consultationMember, setConsultationMember] =
    useState<LocationOpeningsItem | null>(null);

  // Detect desktop vs mobile for the consultation and appointment flows
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    setIsDesktop(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Schedule selection
  const [selectedScheduleId, setSelectedScheduleIdRaw] = useState<
    string | null
  >(() => {
    // Deep-link: if an initial schedule was requested and it exists, use it
    if (
      initialScheduleId &&
      schedules.some((s) => s.id === initialScheduleId)
    ) {
      return initialScheduleId;
    }
    return schedules.length === 1 ? schedules[0].id : null;
  });

  const setSelectedScheduleId = useCallback(
    (id: string | null) => {
      setSelectedScheduleIdRaw(id);
      onScheduleSelect?.(id);
    },
    [onScheduleSelect],
  );

  const selectedSchedule = useMemo(
    () => schedules.find((s) => s.id === selectedScheduleId) ?? null,
    [schedules, selectedScheduleId],
  );

  // Notify parent of initial auto-selection (single schedule or deep-link)
  const hasNotifiedInitialScheduleRef = useRef(false);
  useEffect(() => {
    if (hasNotifiedInitialScheduleRef.current) return;
    hasNotifiedInitialScheduleRef.current = true;

    if (
      initialScheduleId &&
      schedules.some((s) => s.id === initialScheduleId)
    ) {
      onScheduleSelect?.(initialScheduleId);
    } else if (schedules.length === 1 && schedules[0].id) {
      onScheduleSelect?.(schedules[0].id);
    }
  }, [initialScheduleId, onScheduleSelect, schedules]);

  // Date — on the selected location's calendar, the one the openings API
  // reads `date` in (BF-DEC-015).
  const dateTimeZone = bookingTimeZoneFor(
    selectedSchedule ?? (schedules.length === 1 ? schedules[0] : null),
    bookingTimeZone,
  );
  const [date, setDate] = useBookingDate(dateTimeZone);
  const dateString = formatDate(date);

  // Services — fetch the schedule-specific list when a schedule is selected.
  // A failed read never widens back to the whole business catalog
  // (BF-DRIFT-019): services this schedule does not offer are not bookable
  // here, so the picker fails closed and offers a retry instead.
  const unscopedServices = useMemo(
    () => filterBusinessServices(services),
    [services],
  );
  const canScopeCatalog = Boolean(openingsClient.getScheduleDetail);
  const [readCatalog, setReadCatalog] =
    useState<ScheduleCatalog>(UNSCOPED_CATALOG);
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const retryScheduleCatalog = useCallback(
    () => setCatalogAttempt((attempt) => attempt + 1),
    [],
  );

  // Derive the rendered state instead of waiting for the effect below to
  // commit one. A render where a schedule is chosen and the client can scope
  // the catalog is already known to be scoped, so it must never fall through
  // to `unscoped`: the first render of a deep-linked or single schedule (and
  // the server render of the same tree) and every schedule switch otherwise
  // showed the business-wide list, and asked for member openings with a
  // service the schedule may not offer, for one commit.
  const catalog: ScheduleCatalog = useMemo(() => {
    if (!selectedScheduleId || !canScopeCatalog) return UNSCOPED_CATALOG;
    if (
      readCatalog.status !== "unscoped" &&
      readCatalog.scheduleId === selectedScheduleId
    ) {
      return readCatalog;
    }
    return {
      status: "loading",
      scheduleId: selectedScheduleId,
      services: null,
    };
  }, [canScopeCatalog, readCatalog, selectedScheduleId]);

  useEffect(() => {
    const getScheduleDetail = openingsClient.getScheduleDetail;
    // No schedule chosen yet, or a client that cannot scope the catalog at
    // all: the business list is the only list there is.
    if (!selectedScheduleId || !getScheduleDetail) {
      setReadCatalog(UNSCOPED_CATALOG);
      return;
    }
    const scheduleId = selectedScheduleId;
    let cancelled = false;
    // Keep a list this same schedule already returned, through the read and
    // through its failure, so a second failed read does not drop it. Never
    // another schedule's list, and never the business-wide one.
    const keptServices = (prev: ScheduleCatalog) =>
      prev.status !== "unscoped" && prev.scheduleId === scheduleId
        ? prev.services
        : null;
    setReadCatalog((prev) =>
      prev.status === "ready" && prev.scheduleId === scheduleId
        ? prev
        : { status: "loading", scheduleId, services: keptServices(prev) },
    );
    const fail = () =>
      setReadCatalog((prev) => ({
        status: "failed",
        scheduleId,
        services: keptServices(prev),
      }));
    (async () => {
      try {
        const resp = await getScheduleDetail(scheduleId);
        if (cancelled) return;
        if (!resp.ok) {
          fail();
          return;
        }
        const normalized: ServiceDTO[] = resp.data.services.map((s) => ({
          ...s,
          description: s.description ?? "",
          salonId: s.salonId ?? null,
          paymentRequirement: s.paymentRequirement ?? "NONE",
          depositAmount: s.depositAmount ?? null,
          options: (s.options ?? []).map((o) => ({
            ...o,
            description: o.description ?? "",
          })),
        }));
        setReadCatalog({
          status: "ready",
          scheduleId,
          services: filterBusinessServices(normalized),
        });
      } catch {
        if (!cancelled) fail();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedScheduleId, openingsClient, catalogAttempt]);

  const catalogLoading = catalog.status === "loading";
  const catalogFailed = catalog.status === "failed";
  const businessServices =
    catalog.status === "unscoped"
      ? unscopedServices
      : (catalog.services ?? NO_SERVICES);

  const [selectedServices, setSelectedServices] = useState<SelectedService[]>(
    () => (businessServices[0] ? [{ ...businessServices[0] }] : []),
  );

  useEffect(() => {
    setSelectedServices(
      businessServices[0] ? [{ ...businessServices[0] }] : [],
    );
  }, [businessServices]);

  /* Conditional aggregate disclosure, shown before a team member is chosen.
   * Each member's exact effective terms arrive with their openings row, so
   * the catalog can only say that a payment may apply — but staying silent
   * until then would hide the charge behind the slot click
   * (BF-DRIFT-006, booking-flow.instructions.md § 6). It reads the catalog's
   * stated policy only: a clone sets its own price under the inherited
   * policy, so the business price here rules nothing out. A request-required
   * selection discloses the request instead: it precedes any payment. */
  const selectionRequirementNote = useMemo(
    () =>
      resolveCatalogRequirementLabel(selectedServices, { exactTerms: false }),
    [selectedServices],
  );

  const handleBack = useCallback(
    () => setSelectedScheduleId(null),
    [setSelectedScheduleId],
  );

  // Appointment form handler — inline on mobile, drawer overlay on desktop
  const handleOpenAppointmentForm = useCallback(
    (form: BookingAppointmentForm) => {
      bookingCompletedRef.current = false;
      const formWithCompletion: BookingAppointmentForm = {
        ...form,
        onBookingComplete: () => {
          bookingCompletedRef.current = true;
        },
      };
      if (renderInlineAppointment) {
        setInlineAppointment({ form: formWithCompletion, desktop: isDesktop });
      } else {
        openAppointmentForm(formWithCompletion);
      }
    },
    [isDesktop, openAppointmentForm, renderInlineAppointment],
  );

  const handleCloseInlineFlow = useCallback(() => {
    setInlineAppointment(null);
    if (bookingCompletedRef.current) {
      bookingCompletedRef.current = false;
      setRefreshCount((c) => c + 1);
    }
  }, []);

  // Consultation handler — inline on mobile, modal on desktop
  const handleOpenConsultation = useCallback(
    (member: LocationOpeningsItem) => {
      if (isDesktop) {
        setConsultationMember(member);
      } else {
        setInlineConsultation({
          userId: member.userId ?? undefined,
          teamMemberId: member.teamMemberId ?? undefined,
          scheduleId: selectedScheduleId ?? undefined,
          userName: member.name,
          services: selectedServices as ConsultationService[],
        });
      }
    },
    [isDesktop, selectedServices, selectedScheduleId],
  );

  const handleCloseInlineConsultation = useCallback(() => {
    setInlineConsultation(null);
  }, []);

  const hasMultipleSchedules = schedules.length > 1;
  const shouldShowLocationSummary =
    Boolean(selectedSchedule) &&
    (hasMultipleSchedules || showLocationSummary);

  // Register back handler in the mobile drawer header
  const { setDrawerBack } = useDrawerBack();
  useEffect(() => {
    if (inlineAppointmentForm) {
      setDrawerBack(handleCloseInlineFlow);
    } else if (inlineConsultation) {
      setDrawerBack(handleCloseInlineConsultation);
    } else if (hasMultipleSchedules && selectedScheduleId) {
      setDrawerBack(handleBack);
    } else {
      setDrawerBack(null);
    }
    return () => setDrawerBack(null);
  }, [
    inlineAppointmentForm,
    inlineConsultation,
    hasMultipleSchedules,
    selectedScheduleId,
    handleCloseInlineFlow,
    handleCloseInlineConsultation,
    handleBack,
    setDrawerBack,
  ]);

  // No schedules at all
  if (schedules.length === 0) {
    return (
      <div>
        <p>
          Booking is unavailable right now. Please contact the business to
          schedule your appointment.
        </p>
      </div>
    );
  }

  // Mobile inline consultation flow
  if (inlineConsultation && submitServiceRequest && uploadMedia) {
    return (
      <div style={bookingStackGapStyle}>
        <div
          className="openings-appointment-header"
          style={appointmentHeaderStyle}
        >
          <Button
            color="default"
            variant="filled"
            icon={<LeftIcon />}
            onClick={handleCloseInlineConsultation}
          />
          <span style={appointmentHeaderTitleStyle}>Service Request</span>
        </div>
        <ServiceRequestForm
          salonId={salonId}
          userId={inlineConsultation.userId}
          teamMemberId={inlineConsultation.teamMemberId}
          scheduleId={inlineConsultation.scheduleId}
          services={inlineConsultation.services}
          submitRequest={submitServiceRequest}
          uploadMedia={uploadMedia}
          onClose={handleCloseInlineConsultation}
        />
      </div>
    );
  }

  // Mobile inline appointment flow (replaces the booking UI in place). The
  // page header is hidden while this is active (see onInlineFlowActiveChange),
  // so surface our own title + close (X) here — the X dismisses the whole flow
  // and returns to slot selection, so it reads more honestly than a back arrow.
  if (
    inlineAppointment &&
    renderInlineAppointment &&
    !inlineAppointment.desktop
  ) {
    return (
      <div style={bookingStackGapStyle}>
        <div
          className="openings-appointment-header"
          style={appointmentHeaderStyle}
        >
          <span
            style={{
              ...appointmentHeaderTitleStyle,
              fontSize: "1em",
              lineHeight: 1.4,
            }}
          >
            Book an appointment
          </span>
          <Button
            color="default"
            variant="filled"
            icon={<CloseIcon />}
            aria-label="Close"
            onClick={handleCloseInlineFlow}
          />
        </div>
        {renderInlineAppointment(inlineAppointment.form, handleCloseInlineFlow)}
      </div>
    );
  }

  // Multi-schedule: show picker first
  if (hasMultipleSchedules && !selectedScheduleId) {
    return (
      <div style={bookingStackGapStyle}>
        <LocationStep schedules={schedules} onSelect={setSelectedScheduleId} />
      </div>
    );
  }

  return (
    <div style={bookingStackGapStyle}>
      <style>{`
        .openings-location-change-button:focus-visible {
          outline: 2px solid var(--openings-color-text, #111);
          outline-offset: 2px;
        }
        /* The info button has a 40px hit area but a ~16px glyph (and some
           themes pin the glyph to a corner), so ring the glyph, not the box. */
        .openings-member-info-button:focus-visible {
          outline: none;
        }
        .openings-member-info-button:focus-visible > svg {
          outline: 2px solid var(--openings-color-text, #111);
          outline-offset: 2px;
          border-radius: 50%;
        }
      `}</style>
      {/* Schedule header (multi-schedule): title + address, no back chrome.
          The explicit Change action is shown only when another schedule exists. */}
      {shouldShowLocationSummary && selectedSchedule && (
        <div
          className="openings-selected-location"
          style={selectedLocationSummaryStyle}
        >
          <div
            className="openings-selected-location-copy"
            style={selectedLocationCopyStyle}
          >
            <span
              className="openings-selected-location-title"
              style={bookingPageTitleStyle}
            >
              {selectedSchedule.title}
            </span>
            {selectedSchedule.address && (
              <span
                className="openings-selected-location-address"
                style={bookingSmallStyle}
              >
                {selectedSchedule.address}
              </span>
            )}
          </div>
          {hasMultipleSchedules ? (
            <button
              type="button"
              className="openings-location-change-button"
              onClick={handleBack}
              style={locationChangeButtonStyle}
            >
              Change
            </button>
          ) : null}
        </div>
      )}

      {/* Schedule location label (single-schedule, simple template) */}
      {!showLocationSummary && showSchedule && selectedSchedule?.address && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Text style={bookingSectionTitleStyle}>Location</Text>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <p style={{ ...bookingSmallStyle, margin: 0 }}>
              {selectedSchedule.address}
            </p>
          </div>
        </div>
      )}

      {/* Date picker */}
      <section aria-labelledby="appointment-section">
        <div style={bookingColumnStyle}>
          <DateSelector
            label={showAvailabilityStatus ? undefined : "Choose a date"}
            descriptiveInputLabel={showAvailabilityStatus}
            selectedDate={date}
            setDate={setDate}
            filteredWorkDays={[]}
            excludedDates={[]}
            hasBorder={true}
            neutralPalette={showAvailabilityStatus}
            bookingHorizonDays={bookingHorizonDays}
            timeZone={dateTimeZone}
          />
        </div>
      </section>

      {/* Schedule service catalog is still loading */}
      {catalogLoading && (
        <p role="status" style={{ ...bookingSmallStyle, margin: 0 }}>
          Loading services…
        </p>
      )}

      {/* Schedule service catalog could not be read */}
      {catalogFailed && (
        <div
          role="alert"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 8,
          }}
        >
          <p style={{ ...bookingSmallStyle, margin: 0 }}>
            We couldn&apos;t load the services available here. Try again, or
            contact the business.
          </p>
          <button
            type="button"
            className="openings-location-change-button"
            onClick={retryScheduleCatalog}
            style={locationChangeButtonStyle}
          >
            Try again
          </button>
        </div>
      )}

      {/* Service selector */}
      {businessServices.length > 0 && (
        <section aria-labelledby="service-selection-section">
          <SelectService
            label="Choose a service"
            // Location-level: prices vary per provider (member clones), so
            // hide business-level prices in the selector, option modal, and
            // custom-combo modal — each member panel shows its own price.
            showPrice={false}
            defaultServices={businessServices}
            selectedServices={selectedServices}
            setServices={setSelectedServices}
            variant="compact"
            inheritPopupTheme={showAvailabilityStatus}
          />
          {selectionRequirementNote && (
            <p
              className="openings-selection-requirement"
              style={{
                ...bookingSmallStyle,
                margin: "var(--openings-booking-spacing-sm, 8px) 0 0",
              }}
            >
              {selectionRequirementNote}
            </p>
          )}
        </section>
      )}

      {/* Member openings */}
      {selectedScheduleId && businessServices.length > 0 && (
        <section aria-labelledby="openings-section">
          <ScheduleMemberOpeningsDisplay
            salonId={salonId}
            scheduleId={selectedScheduleId}
            date={dateString}
            selectedServices={selectedServices}
            cancellationPolicyEnabled={cancellationPolicyEnabled}
            cancellationPolicyHours={cancellationPolicyHours}
            cancellationPolicyNote={cancellationPolicyNote}
            lateGraceMinutes={lateGraceMinutes}
            verificationMethod={verificationMethod}
            openingsClient={openingsClient}
            openAppointmentForm={handleOpenAppointmentForm}
            onConsultationClick={handleOpenConsultation}
            refreshCount={refreshCount}
            hasTeamMembers={hasTeamMembers}
            showAvailabilityStatus={showAvailabilityStatus}
            memberPageBaseHref={memberPageBaseHref}
          />
        </section>
      )}

      {/* No services available */}
      {businessServices.length === 0 && !catalogLoading && !catalogFailed && (
        <p style={{ ...bookingSmallStyle, fontStyle: "italic" }}>
          No services are currently available for booking. Please contact the
          business directly.
        </p>
      )}

      {/* Desktop consultation drawer */}
      {consultationMember && submitServiceRequest && uploadMedia && (
        <ConsultationDrawer
          salonId={salonId}
          userId={consultationMember.userId ?? undefined}
          teamMemberId={consultationMember.teamMemberId ?? undefined}
          scheduleId={selectedScheduleId ?? undefined}
          services={selectedServices}
          submitRequest={submitServiceRequest}
          uploadMedia={uploadMedia}
          open={true}
          onClose={() => setConsultationMember(null)}
        />
      )}

      {/* Desktop appointment drawer overlay */}
      {inlineAppointment &&
        renderInlineAppointment &&
        inlineAppointment.desktop && (
        <DesktopBookingDrawer open={true} onClose={handleCloseInlineFlow}>
          {renderInlineAppointment(
            inlineAppointment.form,
            handleCloseInlineFlow,
          )}
        </DesktopBookingDrawer>
      )}
    </div>
  );
};

export default React.memo(BookingScheduleBooking);
